/**
 * DialerUIContext — refactor 2026-06-04 (Fowler audit 4/3).
 *
 * Decoupe partielle du DialerContext : extrait les states purement UI
 * (modales, prospect selectionne, dialogues) dans leur propre Provider.
 *
 * Pourquoi : ces states changent souvent (clic sur un prospect, ouverture
 * modale, etc.) et leur changement re-rend TOUS les consommateurs de
 * useDialer() actuellement. En les isolant, on reduit les re-renders
 * inutiles : un composant qui n'a besoin que de selectedProspect n'est
 * plus re-rendu quand activeCalls change.
 *
 * States extraits :
 *  - selectedProspect : prospect ouvert dans la modale plein ecran
 *  - showCallSheet : ancien etat d'overlay (Phase 4)
 *  - connectedContact : qui est dans la modale d'appel en cours
 *  - emailCall : call selectionne pour la modale "Envoyer un email"
 *  - showCelebration : confetti RDV pris
 *  - importDialogOpen : dialog import CSV
 *  - showKeypad / dtmfInput : clavier DTMF
 *  - showNotifications : panel notifications
 *
 * Sources de verite legacy a rationaliser dans une iteration ulterieure
 * (cf. docs/refactor/sources-of-truth.md). Ici on prepare juste le
 * decoupage en deplaçant le state sans changer la logique.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

export interface DialerUIValue {
  // Modale principale (ProspectDetailPanel)
  selectedProspect: any | null;
  setSelectedProspect: (v: any | null) => void;
  // Synchrone : utilise par le bridge inbound + proactive reset useEffect
  selectedProspectRef: React.MutableRefObject<any | null>;

  // Multi-selection (bulk actions)
  selectedProspects: any[];
  setSelectedProspects: (v: any[]) => void;

  // Modale CallSheet legacy (en cours de retrait)
  showCallSheet: boolean;
  setShowCallSheet: (v: boolean) => void;

  // Contact dans la modale d'appel en cours (live transcript + coach)
  connectedContact: any | null;
  setConnectedContact: (v: any | null) => void;
  connectedContactRef: React.MutableRefObject<any | null>;

  // Modale "Envoyer un email" - call selectionne
  emailCall: any | null;
  setEmailCall: (v: any | null) => void;

  // Celebration RDV pris (confetti overlay)
  showCelebration: { name?: string; company?: string } | null;
  setShowCelebration: (v: { name?: string; company?: string } | null) => void;

  // Dialog import CSV
  importDialogOpen: boolean;
  setImportDialogOpen: (v: boolean) => void;

  // Clavier DTMF
  showKeypad: boolean;
  setShowKeypad: (v: boolean) => void;
  dtmfInput: string;
  setDtmfInput: (v: string) => void;

  // Panel notifications
  showNotifications: boolean;
  setShowNotifications: (v: boolean) => void;

  // 2026-06-05 — Dernier prospect contacte (snapshot du dernier selectedProspect
  // non-null). Survit a la fermeture de la modale pour permettre un bouton
  // "Rouvrir [Nom]" dans le FloatingCallBar apres qualif/raccrochage.
  lastProspectContacted: any | null;
  reopenLastProspect: () => void;
  clearLastProspectContacted: () => void;
}

const DialerUIContext = createContext<DialerUIValue | null>(null);

export function DialerUIProvider({ children }: { children: React.ReactNode }) {
  const [selectedProspect, setSelectedProspect] = useState<any | null>(null);
  const [selectedProspects, setSelectedProspects] = useState<any[]>([]);
  const [showCallSheet, setShowCallSheet] = useState(false);
  const [connectedContact, _setConnectedContact] = useState<any | null>(null);
  const [emailCall, setEmailCall] = useState<any | null>(null);
  const [showCelebration, setShowCelebration] = useState<{ name?: string; company?: string } | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const [dtmfInput, setDtmfInput] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [lastProspectContacted, setLastProspectContacted] = useState<any | null>(null);

  // Refs sync pour les hooks/handlers qui ont besoin de lire l'etat
  // courant dans une closure sans attendre le prochain render.
  const selectedProspectRef = useRef<any | null>(null);
  const connectedContactRef = useRef<any | null>(null);

  useEffect(() => {
    selectedProspectRef.current = selectedProspect;
    // Snapshot le prospect des qu'il est selectionne (= modale ouverte).
    // On le garde apres fermeture pour permettre de rouvrir via le bandeau.
    if (selectedProspect) setLastProspectContacted(selectedProspect);
  }, [selectedProspect]);

  const reopenLastProspect = () => {
    if (lastProspectContacted) setSelectedProspect(lastProspectContacted);
  };
  const clearLastProspectContacted = () => setLastProspectContacted(null);

  // Wrap setConnectedContact pour synchroniser la ref AVANT le prochain
  // render (sinon useRollingRefill / handleHangupAndContinue lisent une
  // valeur stale - bug Meilin Zhao 2026-05-15).
  const setConnectedContact = (next: any | null) => {
    const value = typeof next === 'function' ? next(connectedContactRef.current) : next;
    connectedContactRef.current = value;
    _setConnectedContactState(value);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function _setConnectedContactState(v: any | null) { _setConnectedContact(v); }

  const value: DialerUIValue = {
    selectedProspect, setSelectedProspect, selectedProspectRef,
    selectedProspects, setSelectedProspects,
    showCallSheet, setShowCallSheet,
    connectedContact, setConnectedContact, connectedContactRef,
    emailCall, setEmailCall,
    showCelebration, setShowCelebration,
    importDialogOpen, setImportDialogOpen,
    showKeypad, setShowKeypad,
    dtmfInput, setDtmfInput,
    showNotifications, setShowNotifications,
    lastProspectContacted, reopenLastProspect, clearLastProspectContacted,
  };

  return <DialerUIContext.Provider value={value}>{children}</DialerUIContext.Provider>;
}

export function useDialerUI(): DialerUIValue {
  const v = useContext(DialerUIContext);
  if (!v) throw new Error('useDialerUI must be used within DialerUIProvider');
  return v;
}
